import { useList } from "@refinedev/core";
import {
  Row,
  Col,
  Card,
  Space,
  Typography,
  Tag,
  Alert,
  Grid,
} from "antd";
import {
  TeamOutlined,
  CrownOutlined,
  DollarOutlined,
  RobotOutlined,
  FileTextOutlined,
  ApartmentOutlined,
  FlagOutlined,
  HeartOutlined,
  RiseOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useMemo } from "react";
import type {
  User,
  PlanRecord,
  AiMetric,
  Entry,
  Brain,
  FeatureFlag,
  HealthCheck,
} from "@/lib/types";
import { StatCard } from "@/components/StatCard";
import { MelAreaChart, MelDonutChart } from "@/components/charts/MelCharts";
import { brl, usd, intFmt } from "@/lib/format";
import { melColors } from "@/theme/tokens";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

/** Mês corrente (YYYY-MM) para recortar o custo de IA do mês. */
const CURRENT_MONTH = "2026-06";

const HEALTH_STATUS: Record<
  HealthCheck["status"],
  { color: string; icon: React.ReactNode; label: string }
> = {
  up: { color: "green", icon: <CheckCircleOutlined />, label: "UP" },
  degraded: { color: "orange", icon: <WarningOutlined />, label: "DEGRADADO" },
  down: { color: "red", icon: <CloseCircleOutlined />, label: "DOWN" },
};

/**
 * Overview — PÁGINA INICIAL ("/") do admin.
 *
 * Como obtém os dados (MOCK MODE): vários `useList` com `pagination: { mode:
 * "off" }` puxam TODAS as linhas de cada resource (users, plans, ai-metrics,
 * entries, brains, feature-flags, health) e agregamos no cliente em KPIs +
 * gráficos. Nenhuma chamada extra: reusa os mesmos resources/fixtures das telas
 * de detalhe. Com a API real (VITE_MOCK=false) cada lista lê de
 * GET /admin/<resource> (simple-rest) sem mudar a tela.
 */
export const Overview: React.FC = () => {
  const screens = useBreakpoint();

  const offList = (resource: string) => ({
    resource,
    pagination: { mode: "off" as const },
  });

  const { data: usersData, isLoading: lUsers } = useList<User>(offList("users"));
  const { data: plansData, isLoading: lPlans } = useList<PlanRecord>(offList("plans"));
  const { data: aiData, isLoading: lAi } = useList<AiMetric>(offList("ai-metrics"));
  const { data: entriesData, isLoading: lEntries } = useList<Entry>(offList("entries"));
  const { data: brainsData, isLoading: lBrains } = useList<Brain>(offList("brains"));
  const { data: flagsData, isLoading: lFlags } = useList<FeatureFlag>(offList("feature-flags"));
  const { data: healthData, isLoading: lHealth } = useList<HealthCheck>(offList("health"));

  // Guardas: alguns endpoints reais (ai-metrics, health) retornam um OBJETO de
  // agregação, não um array. Sem o Array.isArray o .filter/.map/for..of crasha a
  // tela. Resource não-array → [] (widget mostra 0 até a API/contrato alinhar).
  const asArr = <T,>(d: { data?: unknown } | undefined): T[] =>
    Array.isArray(d?.data) ? (d!.data as T[]) : [];
  const users = useMemo(() => asArr<User>(usersData), [usersData]);
  const plans = useMemo(() => asArr<PlanRecord>(plansData), [plansData]);
  const ai = useMemo(() => asArr<AiMetric>(aiData), [aiData]);
  const entries = useMemo(() => asArr<Entry>(entriesData), [entriesData]);
  const brains = useMemo(() => asArr<Brain>(brainsData), [brainsData]);
  const flags = useMemo(() => asArr<FeatureFlag>(flagsData), [flagsData]);
  const health = useMemo(() => asArr<HealthCheck>(healthData), [healthData]);

  // ── USERS ──
  const userKpis = useMemo(() => {
    const total = users.length;
    const premium = users.filter((u) => u.plano === "premium").length;
    const banned = users.filter((u) => (u as User & { banned?: boolean }).banned).length;
    const premiumPct = total ? (premium / total) * 100 : 0;
    return { total, premium, banned, premiumPct };
  }, [users]);

  // ── BILLING / MRR ──
  const mrr = useMemo(() => {
    const cents = plans
      .filter((p) => p.status === "active" || p.status === "past_due")
      .reduce((s, p) => s + (p.mrr_cents ?? 0), 0);
    const active = plans.filter((p) => p.status === "active").length;
    return { reais: cents / 100, active };
  }, [plans]);

  // ── CUSTO DE IA DO MÊS ──
  const aiKpis = useMemo(() => {
    const monthRows = ai.filter((r) => r.date?.startsWith(CURRENT_MONTH));
    const monthCost = monthRows.reduce((s, r) => s + (r.cost_usd ?? 0), 0);
    const totalCost = ai.reduce((s, r) => s + (r.cost_usd ?? 0), 0);
    const requests = ai.reduce((s, r) => s + (r.requests ?? 0), 0);
    return { monthCost, totalCost, requests };
  }, [ai]);

  // ── CONTEÚDO ──
  const flaggedEntries = useMemo(
    () => entries.filter((e) => e.flagged).length,
    [entries],
  );
  const flaggedBrains = useMemo(
    () => brains.filter((b) => b.flagged).length,
    [brains],
  );
  const activeFlags = useMemo(() => flags.filter((f) => f.enabled).length, [flags]);

  // ── HEALTH ──
  const healthAgg = useMemo(() => {
    let up = 0;
    let degraded = 0;
    let down = 0;
    for (const h of health) {
      if (h.status === "up") up++;
      else if (h.status === "degraded") degraded++;
      else down++;
    }
    const overall: HealthCheck["status"] =
      down > 0 ? "down" : degraded > 0 ? "degraded" : "up";
    return { up, degraded, down, total: health.length, overall };
  }, [health]);

  // ── Série: custo de IA por dia (área, últimos 30d, por kind) ──
  const aiByDay = useMemo(() => {
    const kinds = Array.from(new Set(ai.map((r) => r.kind))).sort();
    const map = new Map<string, Record<string, number | string>>();
    for (const r of ai) {
      const cur = map.get(r.date) ?? { date: r.date.slice(5) };
      cur[r.kind] = Number(((cur[r.kind] as number) ?? 0) + (r.cost_usd ?? 0));
      map.set(r.date, cur);
    }
    const data = Array.from(map.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
    return {
      data,
      series: kinds.map((k) => ({ key: k, name: k.toUpperCase() })),
    };
  }, [ai]);

  // ── Distribuição de planos (rosca) ──
  const planMix = useMemo(
    () => [
      { name: "Premium", value: userKpis.premium },
      { name: "Free", value: userKpis.total - userKpis.premium },
    ],
    [userKpis],
  );

  const anyFlagged = flaggedEntries + flaggedBrains;

  return (
    <div style={{ padding: screens.md ? 24 : 16 }}>
      <Space
        direction="vertical"
        size={4}
        style={{ marginBottom: 16, width: "100%" }}
      >
        <Title level={3} style={{ margin: 0, color: melColors.ink }}>
          Visão geral
        </Title>
        <Text type="secondary">
          Resumo operacional do MEL — usuários, receita, IA, moderação e saúde.
        </Text>
      </Space>

      {/* Banner de saúde do sistema */}
      <Alert
        type={
          healthAgg.overall === "up"
            ? "success"
            : healthAgg.overall === "degraded"
              ? "warning"
              : "error"
        }
        showIcon
        icon={HEALTH_STATUS[healthAgg.overall].icon}
        style={{ marginBottom: 16 }}
        message={
          <Space wrap>
            <Text strong>
              {healthAgg.overall === "up"
                ? "Todos os sistemas operacionais"
                : healthAgg.overall === "degraded"
                  ? "Operando com degradação"
                  : "Incidente em andamento"}
            </Text>
            <Text type="secondary">
              · {healthAgg.up} UP / {healthAgg.degraded} degradado(s) /{" "}
              {healthAgg.down} fora do ar
            </Text>
          </Space>
        }
      />

      {/* Linha 1 de KPIs — usuários, premium, MRR, custo IA do mês */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8} lg={6}>
          <StatCard
            title="Usuários"
            value={userKpis.total}
            prefix={<TeamOutlined style={{ color: melColors.aqua600 }} />}
            hint={`${userKpis.banned} banido(s)`}
            loading={lUsers}
          />
        </Col>
        <Col xs={12} md={8} lg={6}>
          <StatCard
            title="Premium"
            value={userKpis.premium}
            prefix={<CrownOutlined style={{ color: melColors.warning }} />}
            hint={`${userKpis.premiumPct.toFixed(0)}% da base`}
            loading={lUsers}
          />
        </Col>
        <Col xs={12} md={8} lg={6}>
          <StatCard
            title="MRR"
            value={brl.format(mrr.reais)}
            prefix={<DollarOutlined style={{ color: melColors.success }} />}
            hint={`${mrr.active} assinatura(s) ativa(s)`}
            loading={lPlans}
          />
        </Col>
        <Col xs={12} md={8} lg={6}>
          <StatCard
            title="Custo de IA (mês)"
            value={aiKpis.monthCost}
            precision={2}
            prefix={<RobotOutlined style={{ color: "#8B5CF6" }} />}
            hint={`${usd.format(aiKpis.totalCost)} no período`}
            loading={lAi}
          />
        </Col>
      </Row>

      {/* Linha 2 de KPIs — entries, brains, flags ativas, conteúdo sinalizado */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8} lg={6}>
          <StatCard
            title="Entries"
            value={entries.length}
            prefix={<FileTextOutlined style={{ color: melColors.aqua600 }} />}
            hint={`${intFmt.format(aiKpis.requests)} requests de IA`}
            loading={lEntries}
          />
        </Col>
        <Col xs={12} md={8} lg={6}>
          <StatCard
            title="Brains"
            value={brains.length}
            prefix={<ApartmentOutlined style={{ color: melColors.turquoise600 }} />}
            hint="contextos compartilhados"
            loading={lBrains}
          />
        </Col>
        <Col xs={12} md={8} lg={6}>
          <StatCard
            title="Flags ativas"
            value={activeFlags}
            prefix={<FlagOutlined style={{ color: melColors.info }} />}
            hint={`de ${flags.length} no total`}
            loading={lFlags}
          />
        </Col>
        <Col xs={12} md={8} lg={6}>
          <StatCard
            title="Saúde"
            value={healthAgg.total ? `${healthAgg.up}/${healthAgg.total}` : "—"}
            prefix={<HeartOutlined style={{ color: melColors.error }} />}
            hint={
              healthAgg.down + healthAgg.degraded > 0
                ? `${healthAgg.degraded} degradado(s) · ${healthAgg.down} down`
                : "tudo saudável"
            }
            loading={lHealth}
          />
        </Col>
      </Row>

      {/* Aviso de moderação quando há conteúdo sinalizado */}
      {anyFlagged > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<FlagOutlined />}
          style={{ marginBottom: 16 }}
          message={
            <Space wrap>
              <Text strong>Conteúdo sinalizado aguardando revisão</Text>
              <Tag color="orange">{flaggedEntries} entries</Tag>
              <Tag color="orange">{flaggedBrains} brains</Tag>
            </Space>
          }
        />
      )}

      {/* Gráficos */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={16}>
          <Card
            className="mel-glass"
            bordered={false}
            title="Custo de IA por dia (USD)"
            loading={lAi}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                <RiseOutlined style={{ marginRight: 4 }} />
                últimos 30 dias
              </Text>
            }
          >
            <MelAreaChart
              data={aiByDay.data}
              xKey="date"
              series={aiByDay.series}
              valueFormatter={(v) => usd.format(v)}
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card
            className="mel-glass"
            bordered={false}
            title="Distribuição de planos"
            loading={lUsers}
          >
            <MelDonutChart
              data={planMix}
              valueFormatter={(v) => `${intFmt.format(v)} usuários`}
            />
          </Card>
        </Col>
      </Row>

      {/* Resumo de saúde por componente (chips) */}
      <Card
        className="mel-glass"
        bordered={false}
        title="Componentes do sistema"
        loading={lHealth}
      >
        <Space size={[8, 8]} wrap>
          {health.map((h) => {
            const cfg = HEALTH_STATUS[h.status] ?? HEALTH_STATUS.down;
            return (
              <Tag
                key={h.id}
                color={cfg.color}
                icon={cfg.icon}
                style={{ borderRadius: 8, padding: "2px 8px" }}
              >
                <span style={{ textTransform: "capitalize" }}>{h.component}</span>
                {h.latency_ms != null && (
                  <Text type="secondary" style={{ marginLeft: 6, fontSize: 11 }}>
                    {intFmt.format(h.latency_ms)}ms
                  </Text>
                )}
              </Tag>
            );
          })}
          {!lHealth && health.length === 0 && (
            <Text type="secondary">Nenhum componente monitorado.</Text>
          )}
        </Space>
      </Card>
    </div>
  );
};
